import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, FormProvider, type SubmitHandler } from 'react-hook-form'
import GenericModal from '../../../components/GenericModal'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import UserRoleMultiSelect from './UserRoleMultiSelect'
import { RoleManager } from '../../../APIManager'
import { ButtonType } from '../../../components/GenericButton/styles'
import { notification, flashNotification } from '../../../components/Notifications'
import { getCurrentRole, interpretRoleAssignment } from '../../../helpers/roleHelpers'
import { useUserProfile } from '../../../hooks/useUserProfile'

interface User {
  userId: string
  name: string
  email: string
}

interface FormData {
  users: User[]
}

interface AddUserToRoleModalProps {
  open: boolean
  onClose: () => void
  roleName: string
  roleId?: string
}

const AddUserToRoleModal: React.FC<AddUserToRoleModalProps> = ({
  open,
  onClose,
  roleName,
  roleId
}) => {
  const queryClient = useQueryClient()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])

  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []
  const currentLoggedInUserId = userProfile?.user?.userId

  const currentRole = getCurrentRole(currentUserRoles)

  const methods = useForm<FormData>({
    defaultValues: {
      users: []
    }
  })

  const { handleSubmit, reset, watch } = methods
  const users = watch('users')

  const addUsersToRoleMutation = useMutation({
    mutationFn: async (usersToAdd: User[]) => {
      const errors: Array<{ userId: string, error: string }> = []

      // Adding the current user reloads the page in onSuccess, which would tear
      // down a normal snackbar before it can be read — flash those so they
      // survive the reload. Otherwise persist them so they stick until dismissed.
      const willReload = usersToAdd.some((user) => user.userId === currentLoggedInUserId)
      const notify = (message: string, variant: 'success' | 'info' | 'error'): void => {
        if (willReload) {
          flashNotification(message, { variant })
        } else {
          notification(message, { variant, persist: true })
        }
      }

      for (const user of usersToAdd) {
        try {
          const response = await RoleManager.addUserToRole(user.userId, {
            userId: user.userId,
            roleId: roleName
          })
          const { alreadyExists, message } = interpretRoleAssignment(response?.data)
          if (alreadyExists) {
            // Idempotent no-op (BE 200) — the user already held this role.
            notify(message ?? `User ${user.name} already has the ${roleName} role`, 'info')
          } else {
            notify(`User ${user.name} added to ${roleName} role successfully`, 'success')
          }
        } catch (error: any) {
          // 409 (different attributes) / 400 surface the BE guidance text.
          const message = error?.message || 'An unexpected error occurred'
          notify(message, 'error')
        }
      }

      if (errors.length > 0) {
        throw new Error(`Failed to add ${errors.length} user(s)`)
      }

      return usersToAdd
    },
    onSuccess: (data: any) => {
      void queryClient.invalidateQueries({ queryKey: ['roleData'] })
      void queryClient.invalidateQueries({ queryKey: ['rolesSummary'] })
      void queryClient.invalidateQueries({ queryKey: ['roleUsers', roleName] })
      void queryClient.invalidateQueries({ queryKey: ['roleUsers'] })
      void queryClient.invalidateQueries({ queryKey: ['usersSummary'] })
      reset()
      setShowConfirmModal(false)
      setSelectedUsers([])
      onClose()
      const isCurrentLoggedInUserAdded = data.find((user: User) => user.userId === currentLoggedInUserId)
      if (isCurrentLoggedInUserAdded) {
        window.location.reload()
      }
    },
    onError: (error) => {
      console.error('Error adding users to role:', error)
    }
  })

  const onSubmit: SubmitHandler<FormData> = (data) => {
    // Check if System Manager is trying to add roles
    // if (currentRole?.roleName === ROLES.SYSTEM_MANAGER || currentRole?.roleName === 'System Manager') {
    //   notification('System Managers cannot add roles. Please contact an Account Owner.', { variant: 'error' })
    //   return
    // }

    const usersToAdd = data?.users ?? []
    if (usersToAdd.length > 0) {
      setSelectedUsers(usersToAdd)
      setShowConfirmModal(true)
    }
  }

  const handleConfirmSubmit = async (): Promise<void> => {
    if (selectedUsers.length > 0) {
      addUsersToRoleMutation.mutate(selectedUsers)
    }
  }

  const handleClose = (): void => {
    reset()
    setShowConfirmModal(false)
    setSelectedUsers([])
    onClose()
  }

  return (
    <>
      <GenericModal
        isOpen={open && !showConfirmModal}
        title={`Add Users to ${roleName}`}
        onClose={handleClose}
        maxWidth="sm"
        action={false}
        showCloseIcon={true}
      >
        <FormProvider {...methods}>
          <form onSubmit={e => { e.preventDefault(); void handleSubmit(onSubmit)(e) }}>
            <Box display="flex" flexDirection="column" gap={3}>
              <UserRoleMultiSelect
                name="users"
                roleName={roleName}
                label="Select Users"
                placeholder="Search and select users..."
              />
            </Box>

            <DefaultModalActions
              destructiveButtonProps={{
                onClick: handleClose,
                text: 'Cancel'
              }}
              confirmButtonProps={{
                text: 'Next',
                type: 'submit',
                disabled: !users || users.length === 0
              }}
            />
          </form>
        </FormProvider>
      </GenericModal>

      {/* Confirmation Modal */}
      <GenericModal
        isOpen={showConfirmModal}
        title="Confirm Add Users"
        onClose={() => { setShowConfirmModal(false) }}
        maxWidth="sm"
        action={true}
        showCloseIcon={true}
        confirmActionText="Add Users"
        confirmActionType={ButtonType.Primary}
        onConfirm={handleConfirmSubmit}
        confirmButtonLoading={addUsersToRoleMutation.isPending}
        confirmButtonDisabled={addUsersToRoleMutation.isPending}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">
            You are about to add {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} to the <strong>{roleName}</strong> role:
          </Typography>
          <Box
            sx={{
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: 'grey.50',
              borderRadius: 1,
              padding: 2
            }}
          >
            {selectedUsers.map((user, index) => (
              <Typography key={user.userId} variant="body2" sx={{ padding: 1 }}>
                {index + 1}. {user.name} ({user.email})
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Do you want to continue?
          </Typography>
        </Box>
      </GenericModal>
    </>
  )
}

export default AddUserToRoleModal
